import { useDeclarationStats } from "@/features/material-compliance/api/declarationsApi"
import { useInquiryStats } from "@/features/material-compliance/api/supplierInquiriesApi"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Factory } from "lucide-react"
import { useNavigate } from "react-router"
import { StudioCard, type StudioCardStatState } from "./StudioCard"
import { useNumberFormat } from "./useNumberFormat"

export function SupplierStudioCard() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const { format, formatOrNull } = useNumberFormat()
  // Known limitation (spec: degraded states): useDeclarationStats swallows
  // request errors into zeros — its isError is never true. Fixed for real by
  // the Phase 2 summary endpoint.
  const declarations = useDeclarationStats()
  const inquiries = useInquiryStats()

  // "N decisions waiting on you" — declarations read and not yet signed off. It used
  // to read `unresolvedMappings`, which is a different question (declarations whose
  // article mapping came out empty) AND a dead one: that counter filters on a
  // `doc_metadata.pattern` the current pipeline never writes, so the card has been
  // showing 0 whatever the inbox holds. The counter is gone; this is the number the
  // card's own caption has always described.
  const open = declarations.data?.awaitingReview
  const stat: StudioCardStatState =
    open == null
      ? "loading"
      : {
          value: `${format(open)} ${translate("MC_HOME_SUP_STAT_NOUN")}`,
          caption: translate("MC_HOME_SUP_STAT_CAPTION"),
          tone: open > 0 ? "warning" : "success",
        }

  return (
    <StudioCard
      icon={Factory}
      title={translate("MC_TAB_SUPPLIER_STUDIO")}
      question={translate("MC_HOME_SUP_QUESTION")}
      stat={stat}
      statCaption={translate("MC_HOME_SUP_STAT_CAPTION")}
      roleLabel={translate("MC_HOME_SUP_ROLE")}
      badge={
        open
          ? { count: open, tooltip: translate("MC_HOME_SUP_BADGE_TIP") }
          : undefined
      }
      backTitle={translate("MC_HOME_SUP_BACK_TITLE")}
      backRows={[
        {
          label: translate("MC_HOME_SUP_ROW_ON_FILE"),
          value: formatOrNull(declarations.data?.total),
        },
        {
          label: translate("MC_HOME_SUP_ROW_EXPIRING"),
          value: formatOrNull(declarations.data?.expiringSoon),
        },
        {
          label: translate("MC_HOME_SUP_ROW_EXPIRED"),
          value: formatOrNull(declarations.data?.expired),
        },
        {
          label: translate("MC_HOME_SUP_ROW_OPEN_INQ"),
          value: inquiries.isError ? null : formatOrNull(inquiries.data?.open),
        },
      ]}
      ctaLabel={translate("MC_HOME_SUP_CTA")}
      onOpen={() => navigate("/material-compliance/supplier-studio")}
    />
  )
}
